import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRightIcon,
  Avatar,
  BareField,
  BareInput,
  Button,
  Card,
  CarbonAwareBadge,
  EcoFigure,
  EcoHostingIcon,
  EcoPanel,
  Eyebrow,
  Heading,
  IconButton,
  LeafIcon,
  MAP_ROUTES,
  Price,
  SchematicMap,
  SearchIcon,
  ShieldCheckIcon,
  SmolitMark,
  StarIcon,
  Stepper,
  SwapIcon,
} from '@openroad/ui';
import { api, formatDay, formatMoney, formatTime, todayInNewZealand, unwrap } from '../lib/api';

/** How the design words the three steps. */
const STEPS = [
  {
    tint: 'bg-ngahere-tint',
    icon: <SearchIcon size={22} strokeWidth={1.8} className="text-ngahere" />,
    title: 'Search a route',
    body: 'Tell us where and when. Compare drivers by time, price, rating and ride style.',
  },
  {
    tint: 'bg-pohutukawa-tint',
    icon: (
      <span className="text-pohutukawa">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M5 12h14" />
          <path d="M12 5v14" />
        </svg>
      </span>
    ),
    title: 'Book a seat',
    body: 'Reserve in a couple of taps. Pay your share securely and message your driver in-app.',
  },
  {
    tint: 'bg-ngahere-tint',
    icon: <LeafIcon size={22} strokeWidth={1.8} className="text-ngahere" />,
    title: 'Share the journey',
    body: 'Hit the road together, split the cost, and watch your saved CO₂ add up after every trip.',
  },
] as const;

export function HomeScreen() {
  const navigate = useNavigate();
  const [from, setFrom] = useState('Auckland');
  const [to, setTo] = useState('Tauranga');
  const [date, setDate] = useState(todayInNewZealand());
  const [passengers, setPassengers] = useState(1);

  const places = useQuery({
    queryKey: ['places'],
    queryFn: () => unwrap(api.GET('/places')),
    staleTime: 60 * 60 * 1000,
  });

  const popular = useQuery({
    queryKey: ['popular-routes'],
    queryFn: () => unwrap(api.GET('/trips/popular-routes')),
    staleTime: 5 * 60 * 1000,
  });

  const impact = useQuery({
    queryKey: ['community-impact'],
    queryFn: () => unwrap(api.GET('/impact/community')),
    staleTime: 5 * 60 * 1000,
  });

  /** The ride the hero map card advertises: the cheapest on the busiest route. */
  const featured = popular.data?.items?.[0];

  function search(nextFrom = from, nextTo = to) {
    const params = new URLSearchParams({
      from: nextFrom,
      to: nextTo,
      date,
      seats: String(passengers),
    });
    navigate(`/search?${params.toString()}`);
  }

  return (
    <main>
      {/* ---------------------------------------------------------------- hero */}
      <section className="mx-auto max-w-[1280px] px-6 pt-14 pb-2">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <div>
            <div className="mb-[22px] inline-flex items-center gap-2 rounded-full border border-moana-line bg-moana-tint px-3 py-1.5 text-moana-ink">
              <span className="text-tiny font-semibold tracking-eyebrow-lg uppercase">
                Kia ora · Haere mai
              </span>
            </div>

            <h1 className="m-0 mb-[18px] font-display text-hero leading-hero font-bold tracking-hero text-ink">
              Share the open
              <br />
              road across
              <br />
              <span className="text-ngahere">Aotearoa.</span>
            </h1>

            <p className="m-0 mb-8 max-w-[440px] text-title-sm leading-[1.55] text-muted">
              Long-distance carpooling between New Zealand towns and cities. Split the cost of fuel,
              lower your carbon, and travel with people you can trust.
            </p>

            <form
              className="rounded-hero border border-line bg-surface p-2 shadow-search"
              onSubmit={(event) => {
                event.preventDefault();
                search();
              }}
            >
              <div className="flex items-stretch gap-0.5">
                <BareField caption="From">
                  <BareInput
                    list="or-places"
                    value={from}
                    onChange={(event) => setFrom(event.target.value)}
                    placeholder="Leaving from"
                    aria-label="Leaving from"
                  />
                </BareField>
                <IconButton
                  label="Swap origin and destination"
                  size="md"
                  className="self-center"
                  onClick={() => {
                    setFrom(to);
                    setTo(from);
                  }}
                >
                  <SwapIcon size={15} />
                </IconButton>
                <BareField caption="To">
                  <BareInput
                    list="or-places"
                    value={to}
                    onChange={(event) => setTo(event.target.value)}
                    placeholder="Going to"
                    aria-label="Going to"
                  />
                </BareField>
              </div>

              <div className="mx-2 my-0.5 h-px bg-line-soft" />

              <div className="flex flex-wrap items-center gap-0.5 pt-0.5">
                <BareField caption="Date">
                  <BareInput
                    type="date"
                    value={date}
                    onChange={(event) => setDate(event.target.value)}
                    aria-label="Date"
                  />
                </BareField>
                <div className="flex flex-col gap-0.5 px-3.5 py-1.5">
                  <span className="text-tiny font-semibold tracking-eyebrow text-subtle uppercase">
                    Passengers
                  </span>
                  <Stepper
                    value={passengers}
                    onValueChange={setPassengers}
                    min={1}
                    max={4}
                    label="Passengers"
                  />
                </div>
                <Button
                  type="submit"
                  variant="cta"
                  className="m-1 h-auto flex-1 self-stretch px-[26px] py-3"
                  leadingIcon={<SearchIcon size={18} strokeWidth={2.2} />}
                >
                  Search
                </Button>
              </div>
            </form>

            <datalist id="or-places">
              {(places.data?.items ?? []).map((place) => (
                <option key={place.id} value={place.name} />
              ))}
            </datalist>

            <div className="mt-[22px] flex flex-wrap gap-[22px]">
              <span className="flex items-center gap-[7px] text-label text-muted">
                <ShieldCheckIcon size={16} className="text-ngahere" /> Verified members
              </span>
              <span className="flex items-center gap-[7px] text-label text-muted">
                <LeafIcon size={16} className="text-ngahere" /> Carbon-aware
              </span>
              <span className="flex items-center gap-[7px] text-label text-muted">
                <StarIcon size={16} className="text-ngahere" /> 4.9 average rating
              </span>
            </div>
          </div>

          <div className="relative">
            <div className="relative aspect-square overflow-hidden rounded-hero border border-line shadow-hero">
              <SchematicMap route={MAP_ROUTES.hero} />
            </div>
            {featured ? (
              <button
                type="button"
                onClick={() => navigate(`/trips/${featured.sampleTrip.id}`)}
                className="absolute inset-x-[18px] bottom-[18px] flex w-[calc(100%-36px)] cursor-pointer items-center gap-3 rounded-card border border-line bg-surface/95 px-3.5 py-3 text-left shadow-float backdrop-blur-sm"
              >
                <Avatar
                  initials={featured.sampleTrip.driver.initials}
                  colour={featured.sampleTrip.driver.avatarColour}
                  size={38}
                  name={featured.sampleTrip.driver.displayName}
                />
                <span className="flex-1">
                  <span className="block text-body-sm font-bold">
                    {featured.origin.name} → {featured.destination.name}
                  </span>
                  <span className="block text-caption text-muted">
                    {featured.sampleTrip.driver.displayName.split(' ')[0]} ·{' '}
                    {formatDay(featured.sampleTrip.departureAt)}{' '}
                    {formatTime(featured.sampleTrip.departureAt)} ·{' '}
                    <span className="font-semibold text-ngahere">
                      ★ {featured.sampleTrip.driver.ratingAverage ?? '—'}
                    </span>
                  </span>
                </span>
                <Price
                  amount={formatMoney(featured.lowestPrice)}
                  size="compact"
                  caption="per seat"
                />
              </button>
            ) : null}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------- popular routes */}
      <section className="mx-auto max-w-[1280px] px-6 pt-16 pb-2">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <Eyebrow className="mb-1.5">Popular routes</Eyebrow>
            <Heading level="page" as="h2" className="text-[30px]">
              Where Aotearoa is heading
            </Heading>
          </div>
          <button
            type="button"
            onClick={() => navigate('/search')}
            className="flex cursor-pointer items-center gap-1.5 text-body font-semibold text-ngahere"
          >
            Browse all rides <span aria-hidden="true">→</span>
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {popular.isSuccess && popular.data.items.length === 0 ? (
            <Card radius="panel" className="p-6 sm:col-span-2 lg:col-span-3">
              <p className="m-0 text-body text-muted">
                No rides in the next week yet. Be the first —{' '}
                <button
                  type="button"
                  onClick={() => navigate('/publish')}
                  className="cursor-pointer font-semibold text-ngahere"
                >
                  publish one
                </button>
                .
              </p>
            </Card>
          ) : null}
          {(popular.data?.items ?? []).map((route) => (
            <Card
              key={`${route.origin.id}-${route.destination.id}`}
              lift="route"
              onClick={() => search(route.origin.name, route.destination.name)}
            >
              <div className="mb-3.5 flex items-center gap-2.5">
                <span className="font-display text-title-xs font-bold">{route.origin.name}</span>
                <ArrowRightIcon size={16} className="text-pohutukawa" />
                <span className="font-display text-title-xs font-bold">
                  {route.destination.name}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-meta text-muted">
                  {route.ridesThisWeek} {route.ridesThisWeek === 1 ? 'ride' : 'rides'} this week
                </span>
                <span className="text-label">
                  from{' '}
                  <span className="font-extrabold text-ngahere">
                    {formatMoney(route.lowestPrice)}
                  </span>
                </span>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* --------------------------------------------------------- how it works */}
      <section className="mx-auto max-w-[1280px] px-6 pt-16 pb-2">
        <Heading level="page" as="h2" className="mb-7 text-center text-[30px]">
          Three steps to the open road
        </Heading>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {STEPS.map((step) => (
            <Card key={step.title} radius="hero" className="p-7">
              <div
                className={`mb-4 flex h-[46px] w-[46px] items-center justify-center rounded-3xl ${step.tint}`}
              >
                {step.icon}
              </div>
              <Heading level="card" as="h3" className="mb-2 text-[19px]">
                {step.title}
              </Heading>
              <p className="m-0 text-body-lg leading-[1.55] text-muted">{step.body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------------- teasers */}
      <section className="mx-auto max-w-[1280px] px-6 pt-16 pb-2">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.15fr_0.85fr]">
          <EcoPanel
            radius="hero"
            className="cursor-pointer p-[34px]"
            onClick={() => navigate('/eco')}
          >
            {/*
              Zero is a claim, not a placeholder. Falling back to it while the
              request is in flight — or for ever, if it failed — would have the
              home page tell every visitor the community has saved nothing.
            */}
            <EcoFigure
              eyebrow="Community impact"
              value={
                impact.isSuccess ? impact.data.totalKilogramsSaved.toLocaleString('en-NZ') : '—'
              }
              unit="kg CO₂ saved"
              caption={
                impact.isSuccess ? (
                  <>
                    Every shared seat keeps a car off the road. Together, OpenRoad travellers have
                    saved the equivalent of{' '}
                    <strong className="text-white">
                      {impact.data.treesEquivalent.toLocaleString('en-NZ')} trees
                    </strong>{' '}
                    growing for a year.
                  </>
                ) : (
                  'Every shared seat keeps a car off the road. We are adding up what that has come to.'
                )
              }
            />
            <div className="mt-[22px] inline-flex items-center gap-[7px] text-body font-semibold text-white">
              See the impact dashboard <span aria-hidden="true">→</span>
            </div>
            <div className="pointer-events-none absolute right-[-30px] bottom-[-30px] opacity-[0.16]">
              <LeafIcon size={200} strokeWidth={1} className="text-white" />
            </div>
          </EcoPanel>

          <button
            type="button"
            onClick={() => navigate('/smolit')}
            className="relative cursor-pointer overflow-hidden rounded-hero border border-moana-line bg-moana-tint p-[30px] text-left"
          >
            <span className="mb-4 flex animate-float">
              <SmolitMark size={54} />
            </span>
            <h3 className="m-0 mb-2 font-display text-heading-md font-bold tracking-display text-moana-deep">
              Meet Smolit
            </h3>
            <p className="m-0 mb-4 text-body-lg leading-[1.55] text-moana-body">
              Your road-trip companion. Ask in plain words — “find me a ride to Queenstown this
              Friday” — and Smolit does the rest.
            </p>
            <span className="inline-flex items-center gap-[7px] text-body font-bold text-moana-ink">
              Chat with Smolit <span aria-hidden="true">→</span>
            </span>
          </button>
        </div>
      </section>

      {/* ------------------------------------------------------- hosting story */}
      <section className="mx-auto max-w-[1280px] px-6 pt-12">
        <Card radius="hero" className="flex flex-wrap items-center gap-6 p-[30px]">
          <span className="flex h-[52px] w-[52px] flex-none items-center justify-center rounded-3xl bg-ngahere-tint">
            <EcoHostingIcon size={26} className="text-eco" />
          </span>
          <div className="flex-1">
            <Heading level="card" as="h3" className="mb-1 text-title-sm font-semibold">
              Powered by somsorium EcoTechFarms
            </Heading>
            <p className="m-0 max-w-[640px] text-body leading-normal text-muted">
              OpenRoad runs on carbon-aware infrastructure hosted in Aotearoa New Zealand — compute
              that follows clean energy, so the platform itself treads as lightly as the trips it
              shares.
            </p>
          </div>
          <CarbonAwareBadge />
        </Card>
      </section>
    </main>
  );
}
